import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface POItem {
  id?: number;
  product_id: number;
  product_sku: string;
  product_name: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  line_total: number;
}

interface PurchaseOrder {
  id: number;
  po_number: string;
  vendor_id: number;
  vendor_name: string;
  warehouse_id: number;
  warehouse_name: string;
  order_date: string;
  expected_delivery_date: string | null;
  status: string;
  subtotal: number;
  total_amount: number;
  notes: string | null;
  items_count: number;
  items: POItem[];
}

interface Vendor {
  id: number;
  name: string;
}

interface Warehouse {
  id: number;
  name: string;
}

interface ProductSuggestion {
  id: number;
  sku: string;
  name: string;
}

interface PurchaseOrderManagerProps {
  userRole?: string;
}

const STATUS_BADGES: Record<string, { color: string; background: string }> = {
  draft: { color: '#856404', background: '#fff3cd' },
  sent: { color: '#004085', background: '#cce5ff' },
  partial: { color: '#e65100', background: '#fff3e0' },
  received: { color: '#155724', background: '#d4edda' },
  cancelled: { color: '#721c24', background: '#f8d7da' },
};

const PurchaseOrderManager: React.FC<PurchaseOrderManagerProps> = ({ userRole = 'viewer' }) => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState<PurchaseOrder | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});

  // Form state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [formData, setFormData] = useState({
    vendor_id: '',
    warehouse_id: '',
    expected_delivery_date: '',
    notes: '',
  });
  const [formItems, setFormItems] = useState<{ product_id: number; product_sku: string; product_name: string; quantity: string; unit_cost: string }[]>([]);

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [productSuggestions, setProductSuggestions] = useState<ProductSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const canEdit = userRole === 'admin' || userRole === 'manager';

  useEffect(() => { fetchPurchaseOrders(); }, []);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/purchase-orders/');
      setPurchaseOrders(Array.isArray(res.data.purchase_orders) ? res.data.purchase_orders : []);
    } catch (err) {
      console.error('Failed to fetch purchase orders:', err);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormData = async () => {
    try {
      const [vendorRes, warehouseRes] = await Promise.all([
        api.get('/vendors/'),
        api.get('/inventory/warehouses/'),
      ]);
      setVendors(Array.isArray(vendorRes.data) ? vendorRes.data : []);
      setWarehouses(Array.isArray(warehouseRes.data) ? warehouseRes.data : []);
    } catch (err) {
      console.error('Failed to fetch form data:', err);
    }
  };

  const searchProducts = async (query: string) => {
    if (!query.trim()) { setProductSuggestions([]); return; }
    try {
      const res = await api.get('/products/', { params: { q: query, limit: 20 } });
      const products = Array.isArray(res.data.products) ? res.data.products : Array.isArray(res.data) ? res.data : [];
      setProductSuggestions(products.map((p: any) => ({ id: p.id, sku: p.sku, name: p.name })));
    } catch (err) {
      console.error('Product search failed:', err);
      setProductSuggestions([]);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { if (productSearch.trim()) searchProducts(productSearch); }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedOrders = [...purchaseOrders].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    const numFields = ['items_count', 'total_amount', 'subtotal'];
    if (numFields.includes(sortField)) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const openCreate = () => {
    setFormData({ vendor_id: '', warehouse_id: '', expected_delivery_date: '', notes: '' });
    setFormItems([]);
    setProductSearch('');
    setProductSuggestions([]);
    fetchFormData();
    setShowForm(true);
  };

  const addItem = (product: ProductSuggestion) => {
    if (formItems.find(i => i.product_id === product.id)) return;
    setFormItems([...formItems, { product_id: product.id, product_sku: product.sku, product_name: product.name, quantity: '1', unit_cost: '' }]);
    setProductSearch('');
    setProductSuggestions([]);
    setShowSuggestions(false);
  };

  const removeItem = (idx: number) => {
    setFormItems(formItems.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: string, value: string) => {
    const updated = [...formItems];
    (updated[idx] as any)[field] = value;
    setFormItems(updated);
  };

  const handleSubmit = async () => {
    if (!formData.vendor_id || !formData.warehouse_id) {
      alert('Vendor and Warehouse are required');
      return;
    }
    if (formItems.length === 0) {
      alert('At least one item is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        vendor_id: parseInt(formData.vendor_id),
        warehouse_id: parseInt(formData.warehouse_id),
        expected_delivery_date: formData.expected_delivery_date || null,
        notes: formData.notes.trim() || null,
        items: formItems.map(i => ({
          product_id: i.product_id,
          quantity_ordered: parseInt(i.quantity) || 1,
          unit_cost: parseFloat(i.unit_cost) || 0,
        })),
      };
      await api.post('/purchase-orders/', payload);
      setShowForm(false);
      fetchPurchaseOrders();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (po: PurchaseOrder, newStatus: string) => {
    try {
      await api.put(`/purchase-orders/${po.id}`, { status: newStatus });
      fetchPurchaseOrders();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleReceive = async (po: PurchaseOrder) => {
    if (!window.confirm(`Receive PO ${po.po_number}? This will create a GRN.`)) return;
    try {
      await api.post(`/purchase-orders/${po.id}/receive`);
      fetchPurchaseOrders();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to receive PO');
    }
  };

  const handleDelete = async (id: number, poNumber: string) => {
    setDeleteConfirm({show: true, id, name: poNumber});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/purchase-orders/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchPurchaseOrders();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading purchase orders...</div>;
  }

  return (
    <div>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="card-title">Purchase Orders</h2>
        {canEdit && (
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            + New PO
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="table" style={{ fontSize: '13px' }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('po_number')} style={{ cursor: 'pointer' }}>PO # {sortField === 'po_number' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('vendor_name')} style={{ cursor: 'pointer' }}>Vendor {sortField === 'vendor_name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('warehouse_name')} style={{ cursor: 'pointer' }}>Warehouse {sortField === 'warehouse_name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('order_date')} style={{ cursor: 'pointer' }}>Order Date {sortField === 'order_date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('expected_delivery_date')} style={{ cursor: 'pointer' }}>Expected Delivery {sortField === 'expected_delivery_date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('items_count')} style={{ cursor: 'pointer' }}>Items {sortField === 'items_count' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('total_amount')} style={{ cursor: 'pointer' }}>Total {sortField === 'total_amount' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No purchase orders found.
                </td>
              </tr>
            ) : (
              sortedOrders.map(po => (
                <tr key={po.id} onClick={() => setShowDetail(po)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>{po.po_number}</td>
                  <td>{po.vendor_name}</td>
                  <td>{po.warehouse_name}</td>
                  <td>{po.order_date ? new Date(po.order_date).toLocaleDateString() : '-'}</td>
                  <td>{po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : '-'}</td>
                  <td>
                    <span style={{
                      display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                      color: STATUS_BADGES[po.status]?.color || '#334155',
                      background: STATUS_BADGES[po.status]?.background || '#e2e8f0',
                    }}>
                      {po.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>{po.items_count}</td>
                  <td style={{ fontWeight: 500 }}>₹{(po.total_amount || 0).toLocaleString()}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {canEdit && (
                        <select
                          value={po.status}
                          onChange={(e) => handleStatusChange(po, e.target.value)}
                          style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="partial">Partial</option>
                          <option value="received">Received</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                      {canEdit && (po.status === 'sent' || po.status === 'partial') && (
                        <button className="btn btn-primary btn-sm" onClick={() => handleReceive(po)} style={{ fontSize: '11px', padding: '3px 8px' }}>
                          Receive
                        </button>
                      )}
                      {canEdit && (po.status === 'draft' || po.status === 'cancelled') && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(po.id, po.po_number)} style={{ fontSize: '11px', padding: '3px 8px' }}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal" style={{ maxWidth: '700px', width: '95%', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowDetail(null)}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
              aria-label="Close">×</button>

            <h3 style={{ marginBottom: '4px' }}>PO: {showDetail.po_number}</h3>
            <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
              {showDetail.vendor_name} → {showDetail.warehouse_name}
            </p>

            <div className="form-grid-3" style={{ gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Order Date</label>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{showDetail.order_date ? new Date(showDetail.order_date).toLocaleDateString() : '-'}</span>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Expected Delivery</label>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{showDetail.expected_delivery_date ? new Date(showDetail.expected_delivery_date).toLocaleDateString() : '-'}</span>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Status</label>
                <span style={{
                  display: 'inline-block', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                  color: STATUS_BADGES[showDetail.status]?.color || '#334155',
                  background: STATUS_BADGES[showDetail.status]?.background || '#e2e8f0',
                }}>
                  {showDetail.status}
                </span>
              </div>
            </div>

            {showDetail.notes && (
              <div style={{ marginBottom: '16px', fontSize: '13px', color: '#475569', background: '#f8f9fa', padding: '8px 12px', borderRadius: '6px' }}>
                <strong>Notes:</strong> {showDetail.notes}
              </div>
            )}

            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafb', textTransform: 'uppercase', fontSize: '11px', color: '#64748b', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>SKU</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Product</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ordered</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Received</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Unit Cost</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {(showDetail.items || []).map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '11px', color: '#64748b' }}>{item.product_sku}</td>
                    <td style={{ padding: '8px 10px' }}>{item.product_name}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{item.quantity_ordered}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{item.quantity_received}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>₹{(item.unit_cost || 0).toLocaleString()}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>₹{(item.line_total || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '12px', textAlign: 'right', fontSize: '14px', fontWeight: 600 }}>
              Total: ₹{(showDetail.total_amount || 0).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Create PO Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => { if (!saving) setShowForm(false); }}>
          <div className="modal" style={{ maxWidth: '700px', width: '95%', position: 'relative', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { if (!saving) setShowForm(false); }}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
              aria-label="Close">×</button>

            <h3 style={{ marginBottom: '1rem' }}>New Purchase Order</h3>

            <div className="form-grid-2" style={{ gap: '12px', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>Vendor *</label>
                <select value={formData.vendor_id} onChange={e => setFormData({ ...formData, vendor_id: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: 'white' }}>
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>Warehouse *</label>
                <select value={formData.warehouse_id} onChange={e => setFormData({ ...formData, warehouse_id: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: 'white' }}>
                  <option value="">Select Warehouse</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>Expected Delivery Date</label>
                <input type="date" value={formData.expected_delivery_date} onChange={e => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>Notes</label>
                <input type="text" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            </div>

            {/* Items Section */}
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#334155' }}>Items *</label>

              {/* Product Search */}
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <input
                  type="text"
                  value={productSearch}
                  onChange={e => { setProductSearch(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Search products by name or SKU..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                />
                {showSuggestions && productSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
                    border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '200px', overflow: 'auto', zIndex: 100,
                  }}>
                    {productSuggestions.map(p => (
                      <div key={p.id} onClick={() => addItem(p)}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                        <span style={{ fontWeight: 500 }}>{p.name}</span>
                        <span style={{ fontFamily: 'monospace', color: '#64748b', fontSize: '11px', marginLeft: '8px' }}>{p.sku}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Items List */}
              {formItems.length > 0 && (
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginBottom: '8px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafb', textTransform: 'uppercase', fontSize: '11px', color: '#64748b', letterSpacing: '0.5px' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Product</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', width: '80px' }}>Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', width: '100px' }}>Unit Cost</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', width: '100px' }}>Line Total</th>
                      <th style={{ padding: '6px 8px', width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formItems.map((item, idx) => {
                      const lineTotal = (parseInt(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 8px' }}>
                            <div style={{ fontWeight: 500, fontSize: '12px' }}>{item.product_name}</div>
                            <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#64748b' }}>{item.product_sku}</div>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                              style={{ width: '60px', padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type="number" min="0" step="0.01" value={item.unit_cost} onChange={e => updateItem(idx, 'unit_cost', e.target.value)}
                              placeholder="0.00"
                              style={{ width: '80px', padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '12px', fontWeight: 500 }}>
                            ₹{lineTotal.toLocaleString()}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <button onClick={() => removeItem(idx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}
                              aria-label="Remove item">×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {formItems.length === 0 && (
                <div style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', fontSize: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
                  Search and add products above
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="btn btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Creating...' : 'Create PO'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Purchase Order"
        message="Are you sure you want to delete this purchase order? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default PurchaseOrderManager;
